import type { DropEvent, FileDropItem } from "react-aria";

import { useNavigate } from "@tanstack/react-router";
import { CloudUpload, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DropZone, FileTrigger } from "react-aria-components";
import { Controller, useWatch } from "react-hook-form";

import { Button } from "@/components/button";
import { FieldError, Label, TextField } from "@/components/field";
import { useCreateBrandFormContext } from "@/features/brands/components/BrandsForm.tsx";
import { BrandTextField } from "@/features/brands/create/shared/components/brand-text-field/BrandTextField.tsx";
import { CreateBrandPageHeader } from "@/features/brands/create/shared/components/header/CreateBrandPageHeader.tsx";
import { BackIcon } from "@/features/brands/create/shared/components/icons/BackIcon.tsx";
import { UrlInput } from "@/features/brands/create/shared/components/url-input/UrlInput.tsx";
import { useBrandPresignedUpload } from "@/features/brands/shared/hooks/useBrandPresignedUpload";
import { useCheckBrandName } from "@/features/brands/shared/hooks/useCheckBrandName";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId.tsx";

export function BasicInfo({ onNext }: { onNext: () => void }) {
  const navigate = useNavigate();
  const methods = useCreateBrandFormContext();
  const projectId = useGetCurrentProjectId();
  const brandName = useWatch({ control: methods.control, name: "brandName" });
  const [isUploading, setIsUploading] = useState(false);

  const [debouncedName, setDebouncedName] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(brandName || ""), 400);

    return () => clearTimeout(timer);
  }, [brandName]);

  const { data: nameCheck, isFetching: isCheckingName } = useCheckBrandName(
    projectId,
    debouncedName,
  );

  useEffect(() => {
    if (!debouncedName) return;
    if (nameCheck?.exists) {
      methods.setError("brandName", {
        message: "A brand with this name already exists in this project",
      });
    } else if (nameCheck?.exists === false) {
      methods.clearErrors("brandName");
    }
  }, [nameCheck?.exists, debouncedName, methods]);

  return (
    <>
      <CreateBrandPageHeader
        description="We will do all the heavy lifting"
        title="Create a brand"
      />

      <div className="w-full">
        <Controller
          control={methods.control}
          name="logoS3Key"
          render={({ field: { onChange } }) => (
            <BrandLogoUploader
              onS3KeyChange={onChange}
              onUploadingChange={setIsUploading}
            />
          )}
        />
      </div>

      <div className="flex flex-col gap-2 w-full">
        <Controller
          control={methods.control}
          name="brandName"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <BrandTextField
              errorMessage={error?.message}
              isInvalid={invalid}
              label="Brand name"
              name={name}
              onBlur={onBlur}
              onChange={onChange}
              onClear={() => onChange("")}
              placeholder="Enter your brand name"
              ref={ref}
              requiredHint
              showClearButton={(value || "").length > 0}
              value={value || ""}
            />
          )}
        />
      </div>
      <div className="flex flex-col gap-2 w-full -mb-[4px]">
        <Controller
          control={methods.control}
          name="brandWebAddress"
          render={({
            field: { name, onBlur, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <TextField
              className="flex-1"
              isInvalid={invalid}
              name={name}
              validationBehavior="aria"
              value={value || ""}
            >
              <Label>Website</Label>
              <UrlInput
                onBlur={onBlur}
                onChange={onChange}
                onClear={() => onChange("")}
                placeholder="Enter your website link"
                ref={ref}
                showClearButton={(value || "").length > 0}
                value={value || ""}
              />
              <FieldError>{error?.message}</FieldError>
            </TextField>
          )}
        />
      </div>

      <div className="flex justify-center mt-4 w-full">
        <ContinueButton
          isCheckingName={isCheckingName}
          isUploading={isUploading}
          nameExists={nameCheck?.exists}
          onNext={onNext}
        />
      </div>
      <div className="flex justify-center -mt-2 w-full">
        <BackIcon
          action={() => {
            navigate({ to: "/brands" });
          }}
        />
      </div>
    </>
  );
}

function BrandLogoUploader({
  onS3KeyChange,
  onUploadingChange,
}: {
  onS3KeyChange: (key: null | string) => void;
  onUploadingChange: (uploading: boolean) => void;
}) {
  const { isUploading, upload } = useBrandPresignedUpload();

  useEffect(() => {
    onUploadingChange(isUploading);
  }, [isUploading, onUploadingChange]);

  const [file, setFile] = useState<File | null>(null);
  const [isDropTarget, setIsDropTarget] = useState(false);

  const previewUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  useEffect(
    () => () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    },
    [previewUrl],
  );

  const handleFile = async (selectedFile: File) => {
    setFile(selectedFile);

    try {
      const s3Key = await upload(selectedFile, "logo");

      onS3KeyChange(s3Key);
    } catch {
      setFile(null);
      onS3KeyChange(null);
    }
  };

  const handleDrop = async (e: DropEvent) => {
    const fileItem = e.items.find(
      (item): item is FileDropItem => item.kind === "file",
    );
    const droppedFile = await fileItem?.getFile();

    if (droppedFile) {
      handleFile(droppedFile);
    }
  };

  const handleRemove = () => {
    setFile(null);
    onS3KeyChange(null);
  };

  if (file) {
    return (
      <div className="flex w-full items-center justify-between rounded-xl border border-border-brand bg-bg-brand-primary p-4">
        <div className="flex items-center gap-4">
          <div className="grid size-12 place-items-center rounded-lg bg-[#5D574D] overflow-hidden">
            {previewUrl ? (
              <img
                alt={file.name}
                className="size-full object-cover"
                src={previewUrl}
              />
            ) : (
              <span className="text-[10px] font-bold text-white leading-tight px-1 text-center"></span>
            )}
          </div>
          <span className="text-sm font-normal text-text-tertiary">
            {isUploading ? "Uploading..." : file.name}
          </span>
        </div>
        <Button
          isDisabled={isUploading}
          isIconOnly
          onPress={handleRemove}
          size="sm"
          variant="unstyled"
        >
          <Trash2 className="text-text-error-primary" size={16} />
        </Button>
      </div>
    );
  }

  return (
    <DropZone
      className={`group flex w-full cursor-pointer flex-row items-center gap-6 rounded-2xl border transition-all duration-200 px-8 py-6 ${
        isDropTarget
          ? "border-border-brand-solid bg-bg-brand-secondary"
          : "border-border-brand bg-bg-brand-primary hover:bg-bg-brand-secondary"
      }`}
      onDrop={handleDrop}
      onDropEnter={() => setIsDropTarget(true)}
      onDropExit={() => setIsDropTarget(false)}
    >
      <FileTrigger
        acceptedFileTypes={[".png", ".jpg", ".jpeg"]}
        onSelect={(files) => {
          const selectedFile = files ? [...files][0] : null;

          if (selectedFile) handleFile(selectedFile);
        }}
      >
        <Button className="w-full outline-hidden text-left" variant="unstyled">
          <div className="flex items-center gap-6">
            <div className="grid size-12 place-items-center rounded-full bg-bg-brand-secondary text-violet-600 h-14 w-22 mb-8 md:-mr-3 md:h-12 md:w-12 md:mb-4">
              <CloudUpload
                className="text-text-brand-secondary featured-icon-light-fg-brand"
                size={20}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-sm text-text-secondary">
                Upload your brand logo
              </span>
              <span className="text-sm text-text-secondary">
                <span className="text-base font-medium text-text-primary">
                  Click to upload
                </span>{" "}
                or drag and drop
              </span>
              <span className="text-sm text-text-tertiary">
                PNG or JPG up to 10MB
              </span>
            </div>
          </div>
        </Button>
      </FileTrigger>
    </DropZone>
  );
}

function ContinueButton({
  isCheckingName,
  isUploading,
  nameExists,
  onNext,
}: {
  isCheckingName: boolean;
  isUploading: boolean;
  nameExists: boolean | undefined;
  onNext: () => void;
}) {
  const methods = useCreateBrandFormContext();
  const brandName = useWatch({
    control: methods.control,
    name: "brandName",
  });

  return (
    <Button
      className="w-full"
      isDisabled={!brandName || isUploading || isCheckingName || !!nameExists}
      onPress={onNext}
      size="xl"
      variant="primary"
    >
      Continue
    </Button>
  );
}
